import React, { useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { mailService, assistantService } from "../../services/api";
import { newRequestId } from "../../utils/requestId";
import MailboxBrowser from "./MailboxBrowser";
import MailLabelControls from "./MailLabelControls";
import MailAttachments from "./MailAttachments";
import MailWork from "./MailWork";

const input =
  "mt-1 block w-full rounded-md border border-[#dce3dd] bg-white p-2 text-sm";
const field = "block text-sm font-semibold text-[#58635d]";
const deviceZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
const split = (value) =>
  value
    .split(",")
    .map((s) => s.trim().toLowerCase())
    .filter(Boolean);
const display = (value, zone) =>
  value
    ? new Date(value).toLocaleString(undefined, {
        timeZone: zone,
        dateStyle: "medium",
        timeStyle: "short",
      })
    : "Not checked yet";
const localInput = (value) =>
  value
    ? new Date(
        new Date(value).getTime() - new Date(value).getTimezoneOffset() * 60000,
      )
        .toISOString()
        .slice(0, 16)
    : "";
function stamp(value) {
  const iso = new Date(value).toISOString();
  if (localInput(iso) !== value)
    throw new Error("Choose a valid local time outside a daylight-saving gap.");
  return iso;
}
function useRequest() {
  const previous = useRef(null);
  return (payload) => {
    const serialized = JSON.stringify(payload);
    if (previous.current?.serialized !== serialized)
      previous.current = { serialized, id: newRequestId() };
    return { ...payload, request_id: previous.current.id };
  };
}
function useActions() {
  const cache = useQueryClient();
  const [busy, setBusy] = useState(false),
    [error, setError] = useState("");
  const run = async (operation) => {
    if (busy) return;
    setBusy(true);
    setError("");
    try {
      await operation();
      await Promise.all(
        ["mail", "mail-thread", "assistant", "today"].map((key) =>
          cache.invalidateQueries({ queryKey: [key] }),
        ),
      );
    } catch (failure) {
      const detail = failure.response?.data?.detail;
      setError(
        typeof detail === "string"
          ? detail
          : Array.isArray(detail)
            ? detail.map((d) => d.msg).join("; ")
            : failure.message ||
              "Mail action failed. Your entered values are retained.",
      );
    } finally {
      setBusy(false);
    }
  };
  return { busy, error, run };
}

export function MailSources() {
  const navigate = useNavigate();
  const [offset, setOffset] = useState(0);
  const [browse, setBrowse] = useState(false);
  const {
    data,
    error: loadError,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ["mail", offset],
    queryFn: () => mailService.list(offset),
    refetchInterval: 60000,
  });
  const { busy, error, run } = useActions();
  if (isLoading) return <p role="status">Loading selected mail…</p>;
  if (loadError || !data)
    return (
      <div role="alert">
        Mail could not load. <button onClick={() => refetch()}>Retry</button>
      </div>
    );
  return (
    <div className="space-y-5">
      <header>
        <p className="eyebrow">Email follow-through</p>
        <h1 className="page-title">Mail</h1>
        <p className="page-description">
          Read a conversation, prepare a reply, or track when it needs your
          attention.
        </p>
      </header>
      <div className="flex flex-wrap gap-3"><button className="primary-button" aria-expanded={browse} onClick={() => setBrowse(!browse)}>{browse ? "Hide mailbox browser" : "Browse connected Gmail"}</button>
      <Link className="secondary-button" to="/assistant?view=connections">
        Manage Gmail sources
      </Link></div>
      {browse && <MailboxBrowser />}
      {error && (
        <p role="alert" className="text-red-800">
          {error}
        </p>
      )}
      <section className="space-y-3" aria-label="Saved conversations">
        <h2 className="font-bold">Saved conversations</h2>
        {!data.threads.length && (
          <p>
            No conversations opened yet. Browse Gmail or choose a message from a saved search below.
          </p>
        )}
        {data.threads.map((thread) => (
          <Link
            key={thread.id}
            to={`/mail/threads/${thread.id}`}
            className="surface-panel block p-4"
          >
            <h3 className="font-bold">{thread.subject}</h3>
            <p className="text-sm">
              {thread.message_count} messages ·{" "}
              {(thread.followup?.state || thread.watch?.mode)?.replaceAll("_", " ") || "No follow-up set"}
            </p>
            <p className="text-xs">
              Last checked {display(thread.synced_at, data.timezone)}
            </p>
            {thread.followup?.continuous && <p className="text-sm">Continuous tracking · Due {display(thread.followup.due_at, data.timezone)}{!thread.followup.verified ? " · Refresh needed" : ""}</p>}
            {thread.error && <p className="text-red-800">{thread.error}</p>}
          </Link>
        ))}
        <div className="flex gap-3">
          {offset > 0 && (
            <button onClick={() => setOffset(Math.max(0, offset - 30))}>
              Previous conversations
            </button>
          )}
          {data.next_offset != null && (
            <button onClick={() => setOffset(data.next_offset)}>
              More conversations
            </button>
          )}
        </div>
      </section>
      {data.sources.map((source) => (
        <section
          key={source.id}
          className="surface-panel space-y-3 p-5"
          aria-label={source.title}
        >
          <h2 className="font-bold">{source.title}</h2>
          <p className="text-sm">Search: {source.query}</p>
          <p className="text-xs">
            Last synced {display(source.synced_at, data.timezone)}
            {source.limited ? " · Showing the first 20 matching messages" : ""}
          </p>
          {source.error && <p role="alert">{source.error}</p>}
          <button
            disabled={busy}
            className="secondary-button"
            onClick={() => run(() => assistantService.sync(source.id))}
          >
            Refresh search
          </button>
          {source.messages.map((message) => (
            <article key={message.id} className="border-t pt-3">
              <h3 className="font-semibold">
                {message.headers?.find(
                  (h) => h.name.toLowerCase() === "subject",
                )?.value || "(No subject)"}
              </h3>
              <p className="text-xs">
                {
                  message.headers?.find((h) => h.name.toLowerCase() === "from")
                    ?.value
                }
              </p>
              <p className="my-2 text-sm">{message.snippet}</p>
              {message.thread_id ? (
                <button
                  disabled={busy}
                  className="secondary-button"
                  onClick={() =>
                    run(async () => {
                      const thread = await mailService.open(
                        source.id,
                        message.thread_id,
                      );
                      navigate(`/mail/threads/${thread.id}`);
                    })
                  }
                >
                  Open conversation
                </button>
              ) : (
                <p className="text-xs">
                  Refresh this search to retrieve conversation IDs.
                </p>
              )}
            </article>
          ))}
          {!source.messages.length && (
            <p>No matching messages in the saved search.</p>
          )}
        </section>
      ))}
    </div>
  );
}

function ReplyForm({ thread: initial, busy, run, close }) {
  const [thread] = useState(initial);
  const [to, setTo] = useState(
    thread.suggested_recipients.filter((v) => v !== thread.mailbox).join(", "),
  );
  const [cc, setCc] = useState(""),
    [body, setBody] = useState(""),
    [due, setDue] = useState("");
  const request = useRequest();
  return (
    <form
      className="surface-panel space-y-4 p-5"
      onSubmit={(e) => {
        e.preventDefault();
        run(async () => {
          await mailService.reply(
            thread.id,
            request({
              expected_revision: thread.revision,
              message_id: thread.latest_message_id,
              recipients: split(to),
              cc: split(cc),
              body,
              follow_up_at: due ? stamp(due) : null,
            }),
          );
          close();
        });
      }}
    >
      <h2 className="font-bold">Prepare reply</h2>
      <p className="text-sm">
        Subject stays “{thread.subject}” to keep the reply in this conversation.
        Review the suggested recipients before saving.
      </p>
      <label className={field}>
        To
        <input
          required
          className={input}
          value={to}
          onChange={(e) => setTo(e.target.value)}
        />
      </label>
      <label className={field}>
        Cc (optional)
        <input
          className={input}
          value={cc}
          onChange={(e) => setCc(e.target.value)}
        />
      </label>
      <label className={field}>
        Reply message
        <textarea
          required
          rows={6}
          maxLength={20000}
          className={input}
          value={body}
          onChange={(e) => setBody(e.target.value)}
        />
      </label>
      {!thread.watch?.continuous && <label className={field}>
        If unanswered, follow up at (optional)
        <input
          type="datetime-local"
          className={input}
          value={due}
          onChange={(e) => setDue(e.target.value)}
        />
      </label>}
      {thread.watch?.continuous && <p className="text-sm">Continuous tracking will use the saved response windows after Gmail confirms the outgoing reply in a conversation refresh.</p>}
      <p className="text-xs">
        Times entered in {deviceZone}. Waiting starts only after Gmail accepts
        the send. A new message is a prompt to review, not proof the work is
        complete.
      </p>
      <div className="flex gap-3">
        <button disabled={busy} className="primary-button">
          Save unsent reply
        </button>
        <button
          type="button"
          disabled={busy}
          className="secondary-button"
          onClick={close}
        >
          Close reply editor
        </button>
      </div>
    </form>
  );
}

function WatchForm({ thread: initial, followup, busy, run, close }) {
  const [thread] = useState(initial);
  const [mode, setMode] = useState(followup?.continuous ? (followup.state === "waiting" ? "waiting" : "needs_reply") : thread.watch?.mode || "needs_reply"),
    [due, setDue] = useState(localInput(followup?.due_at || thread.watch?.due_at));
  const [continuous, setContinuous] = useState(!!thread.watch?.continuous);
  const [replyHours, setReplyHours] = useState(thread.watch?.reply_within_hours || 24);
  const [waitHours, setWaitHours] = useState(thread.watch?.wait_hours || 72);
  const [senders, setSenders] = useState(
    (thread.watch?.expected_senders || thread.suggested_recipients)
      .filter((v) => v !== thread.mailbox)
      .join(", "),
  );
  const [priority, setPriority] = useState(thread.watch?.priority || "normal");
  const request = useRequest();
  return (
    <form
      className="surface-panel space-y-4 p-5"
      onSubmit={(e) => {
        e.preventDefault();
        run(async () => {
          await mailService.watch(
            thread.id,
            request({
              expected_revision: thread.revision,
              mode,
              due_at: mode === "off" ? null : stamp(due),
              expected_senders: mode === "waiting" || continuous ? split(senders) : [],
              priority,
              continuous: mode !== "off" && continuous,
              ...(mode !== "off" && continuous ? {reply_within_hours: Number(replyHours), wait_hours: Number(waitHours)} : {}),
            }),
          );
          close();
        });
      }}
    >
      <h2 className="font-bold">Track this conversation</h2>
      <label className={field}>
        Follow-up type
        <select
          className={input}
          value={mode}
          onChange={(e) => setMode(e.target.value)}
        >
          <option value="needs_reply">I need to reply</option>
          <option value="waiting">Waiting for someone’s reply</option>
          <option value="off">Stop tracking / reviewed</option>
        </select>
      </label>
      {mode !== "off" && (
        <>
          <label className={field}>
            Follow-up deadline
            <input
              required
              type="datetime-local"
              className={input}
              value={due}
              onChange={(e) => setDue(e.target.value)}
            />
          </label>
          <p className="text-xs">
            Enter time in {deviceZone}. Background monitoring must be enabled in
            Assistant settings.
          </p>
          <label className={field}>
            Priority
            <select
              className={input}
              value={priority}
              onChange={(e) => setPriority(e.target.value)}
            >
              {["normal", "high", "critical", "low"].map((p) => (
                <option key={p}>{p}</option>
              ))}
            </select>
          </label>
        </>
      )}
      {mode !== "off" && <section className="space-y-3" aria-label="Continuous follow-up settings">
        <label className="flex items-center gap-2 text-sm font-semibold"><input type="checkbox" checked={continuous} onChange={e => setContinuous(e.target.checked)} />Keep tracking each reply cycle</label>
        {continuous && <>
          <p className="text-sm">After a selected participant replies, remind me to review it. After I respond to a selected participant, wait for their next reply. Additional messages from the same side do not extend the current deadline. Linked work stays unchanged.</p>
          <div className="grid gap-3 sm:grid-cols-2">
            <label className={field}>Review incoming replies within (hours)<input className={input} type="number" required min="1" max="720" step="1" value={replyHours} onChange={e => setReplyHours(e.target.value)} /></label>
            <label className={field}>Follow up on unanswered replies after (hours)<input className={input} type="number" required min="1" max="720" step="1" value={waitHours} onChange={e => setWaitHours(e.target.value)} /></label>
          </div>
          <p className="text-xs">Saving starts from the currently displayed conversation and the deadline above. New reply cycles use these elapsed-hour windows. Refresh first if messages or their order need review. Stop tracking when this conversation is finished.</p>
        </>}
      </section>}
      {mode !== "off" && (mode === "waiting" || continuous) && (
        <label className={field}>
          Expected senders (comma-separated)
          <input
            required
            className={input}
            value={senders}
            onChange={(e) => setSenders(e.target.value)}
          />
        </label>
      )}
      <div className="flex gap-3">
        <button disabled={busy} className="primary-button">
          Save follow-up
        </button>
        <button
          type="button"
          disabled={busy}
          className="secondary-button"
          onClick={close}
        >
          Close follow-up editor
        </button>
      </div>
    </form>
  );
}

export function MailThreadWorkspace({ threadId }) {
  const {
    data,
    error: loadError,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ["mail-thread", threadId],
    queryFn: () => mailService.get(threadId),
    refetchInterval: 30000,
  });
  const { busy, error, run } = useActions();
  const [reply, setReply] = useState(false),
    [watch, setWatch] = useState(false);
  const [analysisNotice, setAnalysisNotice] = useState("");
  if (isLoading) return <p role="status">Loading conversation…</p>;
  if (loadError || !data)
    return (
      <div role="alert">
        Conversation unavailable.{" "}
        <button onClick={() => refetch()}>Retry</button>
        <Link to="/mail">Back to mail</Link>
      </div>
    );
  const thread = data.thread;
  return (
    <div className="space-y-5">
      <Link className="text-sm font-semibold" to="/mail">
        ← Mail
      </Link>
      <header>
        <p className="eyebrow">Email follow-through</p>
        <h1 className="page-title">{thread.subject}</h1>
        <p className="text-sm">Mailbox: {thread.mailbox}</p>
        <p className="text-xs">
          Last checked {display(thread.synced_at, data.timezone)} ·{" "}
          {data.followup_state.replaceAll("_", " ")}
        </p>
      </header>
      {(error || thread.error) && (
        <p role="alert" className="surface-panel p-4 text-red-800">
          {error || thread.error}
        </p>
      )}
      <div className="flex flex-wrap gap-3">
        <button
          disabled={busy}
          className="secondary-button"
          onClick={() => run(() => mailService.refresh(threadId))}
        >
          Refresh conversation
        </button>
        <button
          disabled={busy || !thread.latest_message_id}
          className="primary-button"
          onClick={() => setReply(true)}
        >
          Prepare reply
        </button>
        <button
          disabled={busy}
          className="secondary-button"
          onClick={() => setWatch(true)}
        >
          Manage follow-up
        </button>
        {data.analysis_enabled && <button className="secondary-button" disabled={busy} onClick={() => run(async () => {
          setAnalysisNotice("");
          const result = await mailService.analyse(threadId);
          setAnalysisNotice(result.message);
        })}>Refresh and analyse</button>}
        <a
          className="secondary-button"
          href={thread.url}
          target="_blank"
          rel="noreferrer"
        >
          Open Gmail
        </a>
      </div>
      {analysisNotice && <p role="status" className="text-sm">{analysisNotice} <Link to="/assistant?view=agent" className="underline">Open Agent results</Link></p>}
      {data.analysis_enabled && data.analysis_reason && <p className="surface-panel p-4 text-sm">{data.analysis_reason}</p>}
      {data.followup?.continuous && <section className="surface-panel p-4 space-y-2" aria-label="Continuous follow-up">
        <h2 className="font-bold">Continuous follow-up</h2>
        <p className="text-sm">{data.followup.state === "waiting" ? "Waiting for a selected participant" : data.followup.state === "needs_review" ? "Review the conversation before continuing" : data.followup.state === "reply_received" ? "Review the new reply" : "Your reply is due"} · {display(data.followup.due_at, data.timezone)}</p>
        <p className="text-xs">Review incoming replies within {thread.watch.reply_within_hours} hours; follow up after {thread.watch.wait_hours} hours without a response. Repeated messages from the same side keep the current deadline.</p>
        {!data.followup.verified && <p role="status" className="text-amber-800 text-sm">Refresh needed. The last observed reply state is retained until this conversation is checked successfully.</p>}
        {data.followup.issue && <p role="alert" className="text-amber-800 text-sm">{data.followup.issue}</p>}
        <p className="text-xs">Only messages involving your selected participants advance tracking. Automated replies are ignored. No message completes linked work or sends a reply for you.</p>
      </section>}
      {!data.followup?.continuous && thread.watch?.mode && thread.watch.mode !== "off" && (
        <p className="surface-panel p-4 text-sm">
          {data.followup_state === "reply_received"
            ? "A reply has arrived for your review."
            : data.followup_state === "reply_sent"
              ? "Gmail shows your outgoing reply."
              : `${thread.watch.mode === "waiting" ? "Waiting for " + thread.watch.expected_senders.join(", ") : "Your reply is due"} · ${display(thread.watch.due_at, data.timezone)}.`}{" "}
          A response from any expected sender prompts review. Automated/list
          messages do not count as replies. Review any new response before
          deciding whether the work is finished.
        </p>
      )}
      {reply && (
        <ReplyForm
          thread={thread}
          busy={busy}
          run={run}
          close={() => setReply(false)}
        />
      )}
      {watch && (
        <WatchForm
          thread={thread}
          followup={data.followup}
          busy={busy}
          run={run}
          close={() => setWatch(false)}
        />
      )}
      {data.mailbox_controls && <MailLabelControls key={`labels-${thread.id}`} thread={thread} controls={data.mailbox_controls} timezone={data.timezone} />}
      <MailWork key={`work-${thread.id}`} thread={thread} timezone={data.timezone} />
      <section aria-label="Conversation messages" className="space-y-3">
        <h2 className="font-bold">Messages · {thread.messages.length}</h2>
        <p className="text-xs">
          Text view; remote images are not loaded. Attachments download only when selected.
        </p>
        {thread.messages.map((m) => (
          <article className="surface-panel space-y-2 p-5" key={m.id}>
            <h3 className="font-bold">{m.headers.from || "Unknown sender"}</h3>
            <p className="text-xs">
              {display(m.at, data.timezone)} ·{" "}
              {m.outgoing ? "Outgoing" : "Incoming"}
              {m.automatic ? " · Automated/list message" : ""}
              {m.labels.some((l) => ["TRASH", "SPAM", "DRAFT"].includes(l))
                ? " · Excluded from follow-up signals"
                : ""}
            </p>
            <p className="text-xs">
              To: {m.headers.to || "Not provided"}
              {m.headers.cc ? " · Cc: " + m.headers.cc : ""}
            </p>
            {m.headers["reply-to"] && (
              <p className="text-xs">Reply-To: {m.headers["reply-to"]}</p>
            )}
            <div className="whitespace-pre-wrap break-words text-sm">
              {m.text ||
                "No readable inline text. Open Gmail for this message."}
            </div>
            {m.truncated && (
              <p className="text-amber-800 text-sm">
                Message text is incomplete. Open Gmail for the full content.
              </p>
            )}
            <MailAttachments threadId={thread.id} message={m} threadRevision={thread.revision} reviews={data.attachment_reviews || []} onReviewChange={refetch} />
          </article>
        ))}
      </section>
      <section aria-label="Reply drafts and send results" className="space-y-3">
        <h2 className="font-bold">Reply drafts and send results</h2>
        {!data.actions.length && <p>No replies drafted here yet.</p>}
        {data.actions.map((a) => (
          <article key={a.id} className="surface-panel space-y-3 p-5">
            <div className="flex justify-between gap-3">
              <h3 className="font-bold">{a.title}</h3>
              <span className="status-pill">
                {a.status.replaceAll("_", " ")}
              </span>
            </div>
            <p className="text-sm">
              To: {a.payload.recipients.join(", ")}
              {a.payload.cc?.length ? " · Cc: " + a.payload.cc.join(", ") : ""}
            </p>
            <p className="whitespace-pre-wrap text-sm">{a.payload.body}</p>
            {a.payload.follow_up_at && (
              <p className="text-xs">
                If unanswered, follow up{" "}
                {display(a.payload.follow_up_at, data.timezone)}
              </p>
            )}
            {a.result?.message && (
              <p role="status" className="text-sm">
                {a.result.message}
              </p>
            )}
            {a.status === "draft" && (
              <div className="flex gap-3">
                <button
                  disabled={busy}
                  className="primary-button"
                  onClick={() => run(() => assistantService.execute(a.id))}
                >
                  Send this reply
                </button>
                <button
                  disabled={busy}
                  className="secondary-button"
                  onClick={() => run(() => assistantService.cancel(a.id))}
                >
                  Cancel draft
                </button>
              </div>
            )}
            {["sending", "needs_review"].includes(a.status) && (
              <button
                disabled={busy}
                className="secondary-button"
                onClick={() => run(() => mailService.reconcile(a.id))}
              >
                Check Gmail Sent result
              </button>
            )}
          </article>
        ))}
      </section>
    </div>
  );
}

export default function MailPage() {
  const { threadId } = useParams();
  return (
    <main className="app-page">
      <div className="app-page-inner">
        {threadId ? (
          <MailThreadWorkspace key={threadId} threadId={Number(threadId)} />
        ) : (
          <MailSources />
        )}
      </div>
    </main>
  );
}
